import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export const HTTP_HEADERS = {
  CONTENT_TYPE: 'Content-Type',
  AUTHORIZATION: 'Authorization',
  ACCEPT: 'Accept',
  X_API_KEY: 'x-api-key',
  CHANNEL: 'Channel',
  CACHE_CONTROL: 'Cache-control',
  EXPIRES: 'Expires',
  PRAGMA: 'Pragma',
  COMPILAR_SIN_HEADERS_GLOBALES: 'COMPILAR_SIN_HEADERS_GLOBALES'
};

export type HttpHeadersPortal = Partial<Record<keyof typeof HTTP_HEADERS, string>>;

@Injectable({
  providedIn: 'root',
})
export class HttpService {
  constructor(private http: HttpClient) {}

  /**
   * Retrasa la resolución de una Promise.
   * @param obj Objeto que se desea resolver.
   * @param delay Tiempo en milisegundos que se retrasa la resolución de la promise.
   */
  public delayedPromiseResolve(obj: any, delay: number): Promise<any> {
    return new Promise<any>((resolve) => {
      setTimeout(() => resolve(obj), delay);
    });
  }

  /**
   * Ejecuta una petición tipada basada en el verbo de petición pasado por parámetro.
   * @param method ('get' | 'post' | 'put' | 'delete') verbo de petición.
   * @param host (string) host principal de la petición.
   * @param endpoint (string) url destino de la petición.
   * @param paramsQuery (any) parámetros opcionales para el query string de la petición.
   * @param paramsBody (any) parámetros para el body de la petición.
   * @param headers (object) parámetros para el header de la petición.
   * @param customOptions (object) parámetros para el header de la petición.
   */
  public request<T>(
    method: 'get' | 'post' | 'put' | 'delete' | 'patch',
    host: string,
    endpoint: string,
    paramsQuery: any,
    paramsBody: any,
    headers: HttpHeadersPortal,
    customOptions?: any
  ): Observable<T> {
    const url = host + endpoint;
    const options = {
      params: this.buildParams(paramsQuery),
      headers: this.buildHeaders(headers),
      ...customOptions
    };

    let requestObservable: Observable<T | HttpEvent<T>>;

    switch (method) {
      case 'get':
        requestObservable = this.http.get<T>(url, options);
        break;
      case 'post':
        requestObservable = this.http.post<T>(url, paramsBody, options);
        break;
      case 'put':
        requestObservable = this.http.put<T>(url, paramsBody, options);
        break;
      case 'patch':
        requestObservable = this.http.patch<T>(url, paramsBody, options);
        break;
      case 'delete':
        requestObservable = this.http.delete<T>(url, options);
        break;
      default:
        return throwError('Método HTTP no soportado');
    }

    return requestObservable.pipe<T, any>(
      map<T, any>(this.unserializeResponse),
      catchError<T, any>(this.handleError)
    );
  }

  /**
   * Realiza una petición de tipo GET.
   * @param host (string) host principal de la petición.
   * @param endpoint (string) url destino de la petición.
   * @param paramsQuery (any) parámetros que se envían en el querystring de la petición.
   * @param headers (object) parámetros para el header de la petición.
   * @param customOptions (object) parámetros para personalizar el mapeo de respuesta, su tipo, etc.
   */
  public get<T>(host: string, endpoint: string, paramsQuery: any = {}, headers?: HttpHeadersPortal, customOptions?: object): Observable<T> {
    return this.request<T>('get', host, endpoint, paramsQuery, null, headers, customOptions);
  }

  /**
   * Realiza una petición de tipo POST.
   * @param host (string) host principal de la petición.
   * @param endpoint (string) url destino de la petición.
   * @param body (objeto) contenido que se envían en el body de la petición.
   * @param paramsQuery (object) parámetros que se envían en el querystring de la petición.
   * @param headers (object) parámetros para el header de la petición.
   * @param customOptions (object) parámetros para personalizar el mapeo de respuesta, su tipo, etc.
   */
  public post<T>(host: string, endpoint: string, body: object = {}, paramsQuery?: object, headers?: HttpHeadersPortal, customOptions?: object): Observable<T> {
    return this.request<T>('post', host, endpoint, paramsQuery, body, headers, customOptions);
  }

  /**
   * Realiza una petición de tipo PUT.
   * @param host (string) host principal de la petición.
   * @param endpoint (string) url destino de la petición.
   * @param body (any) parámetros que se envían en el body de la petición.
   * @param paramsQuery (object) parámetros que se envían en el querystring de la petición.
   * @param headers (object) parámetros para el header de la petición.
   */
  public put<T>(host: string, endpoint: string, body: object, paramsQuery?: object, headers?: HttpHeadersPortal, customOptions?: object): Observable<T> {
    return this.request<T>('put', host, endpoint, paramsQuery, body, headers, customOptions);
  }

    /**
   * Realiza una petición de tipo PUT.
   * @param host (string) host principal de la petición.
   * @param endpoint (string) url destino de la petición.
   * @param body (any) parámetros que se envían en el body de la petición.
   * @param paramsQuery (object) parámetros que se envían en el querystring de la petición.
   * @param headers (object) parámetros para el header de la petición.
   */
    public patch<T>(host: string, endpoint: string, body: object, paramsQuery?: object, headers?: HttpHeadersPortal): Observable<T> {
      return this.request<T>('put', host, endpoint, paramsQuery, body, headers);
    }

  /**
   * Método de petición usando el verbo DELETE.
   * @param host (string) host principal de la petición.
   * @param endpoint (string) url destino de la petición.
   * @param paramsQuery (any)
   * @param headers (object) parámetros para el header de la petición.
   */
  public delete<T>(host: string, endpoint: string, paramsQuery?: any, headers?: HttpHeadersPortal): Observable<T> {
    return this.request<T>('delete', host, endpoint, paramsQuery, null, headers);
  }

  /**
   * Deserializa la respuesta de la petición.
   * @param resp (Response|any) Objeto de respuesta de la petición.
   */
  private unserializeResponse<T>(resp: any): T {
    return resp; // Angular HttpClient ya maneja la deserialización JSON.
  }

  /**
   * Construye los parámetros que se van a enviar en la petición.
   * @param params (any) Array de parámetros que se enviarán en la petición.
   */
  private buildParams(params: any): HttpParams {
    let httpParams = new HttpParams();
    if (params) {
      for (const key of Object.keys(params)) {
        httpParams = httpParams.set(key, params[key]);
      }
    }
    return httpParams;
  }

  /**
   * Construye parámetros personalizados para el header de la petición.
   * @param headers objeto con los parámetros para agregarle al header del request.
   */
  private buildHeaders(headers?: HttpHeadersPortal): HttpHeaders {
    const globalHeaders = this.getGlobalHeaders();
    const compilarSinHeadersGlobales = (headers && 
                                        headers[HTTP_HEADERS.COMPILAR_SIN_HEADERS_GLOBALES] && 
                                        headers[HTTP_HEADERS.COMPILAR_SIN_HEADERS_GLOBALES] === 'true');
    if (compilarSinHeadersGlobales) {
      delete headers[HTTP_HEADERS.COMPILAR_SIN_HEADERS_GLOBALES]
      return new HttpHeaders(headers);
    }

    if (headers) {
      for (const key of Object.keys(headers) as (keyof typeof HTTP_HEADERS)[]) {
        if (headers[key]) {
          globalHeaders[key] = headers[key];
        }
      }
    }

    return new HttpHeaders(globalHeaders);
  }

  public getGlobalHeaders() {
    const globalHeaders = { 
      [HTTP_HEADERS.X_API_KEY] : '123456789!aA' 
    };
  
    return globalHeaders;
  }

  /**
   * Maneja los errores de las peticiones HTTP.
   * @param error Error de la petición HTTP.
   */
  private handleError(error: HttpErrorResponse) {
    let errorMessage = 'Error desconocido';
    if (error.error instanceof ErrorEvent) {
      // Error del lado del cliente
      errorMessage = `Error de app: ${error.error.message}`;
    } else {
      // Error del lado del servidor
      errorMessage = `Error de server: ${error.status}\nMensaje: ${error.message}`;
    }
    console.error(errorMessage);
    return throwError(errorMessage);
  }

  public downloadFileFromGet(url: string, endpoint: string, paramsQuery?: object, headers?: HttpHeadersPortal, reportProgress = true) {
    const customOptions = {
      observe: 'response', 
      responseType: 'blob',
      reportProgress
    };

    return this.get(url, endpoint, paramsQuery, headers, customOptions).pipe(
      map(response => { return response; })
    );
  }

  public downloadFileFromPost(url: string, endpoint: string, body?: object, paramsQuery?: object, headers?: HttpHeadersPortal, reportProgress = true) {
    const customOptions = {
      observe: 'response', 
      responseType: 'blob',
      reportProgress
    };

    return this.post(url, endpoint, body, paramsQuery, headers, customOptions).pipe(
      map(response => { return response; })
    );
  }

  public getRealTimeData<T>(url: string, headers?: HttpHeadersPortal): Observable<T> {
    return new Observable<T>(observer => {
        const eventSource = new EventSource(url);

        eventSource.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);
                observer.next(data);
            } catch (error) {
                observer.error(error);
            }
        };

        eventSource.onerror = (error) => {
            observer.error(error);
        };

        return () => eventSource.close(); // Función de limpieza
    });
  }

  public getHeadersSinGlobales(): HttpHeadersPortal {
    return  {
      COMPILAR_SIN_HEADERS_GLOBALES: 'true'
    };
  }
}